import { BuildingsIcon, GearSixIcon } from '@phosphor-icons/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { ClientProfileResponse } from '@/api/clients/get-client-profile'
import { FieldsTable } from '@/components/layout/field-table'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { ZoomableImage } from '@/components/ui/zoomable-image'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { MailingListItem } from '@/models'
import { UserRoleEnum } from '@/models/features/user.models'
import { APP_USER_CUTOFF_DATE_MS } from '@/pages/clients/clients-table/clients-table.constants'
import { ResidentVehiclesModal } from '@/pages/parking-management/residents-parking/parking-modals'
import { formatDateLong, formatDateOnly } from '@/utils/date'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import {
  canChangeBuilding,
  getPhonePmsTooltip,
  getPmsTooltip,
  isFieldSynced,
  isFieldVisible,
  MAX_VISIBLE_CHIPS,
  PROTECTED_MAILING_LIST_IDS,
  ProfileBeFieldEnum,
} from './general-information.constants'
import * as S from './general-information.styled'
import { formatBooleanValue, formatDateOfBirth, type PmsIdInfo } from './general-information.utils'
import { GeneralInformationGroup } from './general-information-group'

interface PmsIndicatorProps {
  tooltip: string
}

export const PmsIndicator = ({ tooltip }: PmsIndicatorProps) => (
  <Tooltip
    trigger={
      <S.PmsIconWrapper>
        <Icon icon={BuildingsIcon} size="1.6rem" />
      </S.PmsIconWrapper>
    }
  >
    {tooltip}
  </Tooltip>
)

interface LockedFieldRowProps {
  label: string
  value: React.ReactNode
  isSynchronized: boolean
  pmsTooltip?: string
}

export const LockedFieldRow = ({
  label,
  value,
  isSynchronized,
  pmsTooltip,
}: LockedFieldRowProps) => {
  const { t } = useTranslation(['clients'])

  return (
    <S.FieldRow>
      <S.LabelWrapper>
        <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
          {label}
        </Typography>
        {isSynchronized && <PmsIndicator tooltip={pmsTooltip ?? getPmsTooltip(t)} />}
      </S.LabelWrapper>
      <S.FieldValue $isViewOnly>{value || EMPTY_VALUE}</S.FieldValue>
    </S.FieldRow>
  )
}

interface GeneralInformationViewFieldsProps {
  clientData: ClientProfileResponse
  clientMailingLists: MailingListItem[]
  hiddenFields: string[]
  syncedFields: string[]
  showInHouseDelivery: boolean
  pmsIdInfo: PmsIdInfo | null
  userRole: string | null
}

export const GeneralInformationViewFields = ({
  clientData,
  clientMailingLists,
  hiddenFields,
  syncedFields,
  showInHouseDelivery,
  pmsIdInfo,
  userRole,
}: GeneralInformationViewFieldsProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const isAppUser = (clientData.lastAppLogin ?? 0) >= APP_USER_CUTOFF_DATE_MS

  const sortedMailingLists = [...clientMailingLists].sort((a, b) => {
    const aLocked = PROTECTED_MAILING_LIST_IDS.includes(String(a.id))
    const bLocked = PROTECTED_MAILING_LIST_IDS.includes(String(b.id))
    return aLocked === bLocked ? 0 : aLocked ? -1 : 1
  })

  const photoSrc = clientData.user.avatar
    ? getAssetLink({ file: clientData.user.avatar, path: 'chitown-avatar/avatar' })
    : null

  return (
    <FieldsTable>
      <GeneralInformationGroup
        group="personalAndContact"
        hiddenFields={hiddenFields}
        userRole={userRole}
      >
        {/* Photo */}
        {isFieldVisible(null, hiddenFields) && (
          <S.PhotoRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.photo.label')}
              </Typography>
              {isFieldSynced(null, syncedFields) && <PmsIndicator tooltip={getPmsTooltip(t)} />}
            </S.LabelWrapper>
            {/* TODO: Replace with PhotoUpload component (new component per requirements) */}
            {photoSrc ? (
              <ZoomableImage
                src={photoSrc}
                alt={t('clients:generalInformation.photo.alt')}
                title={t('clients:generalInformation.photo.label')}
              />
            ) : (
              <S.PhotoPlaceholder>—</S.PhotoPlaceholder>
            )}
          </S.PhotoRow>
        )}

        {/* First name */}
        {isFieldVisible(ProfileBeFieldEnum.enum.firstName, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.firstName.label')}
            value={clientData.user.firstName}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.firstName, syncedFields)}
          />
        )}

        {/* Last name */}
        {isFieldVisible(ProfileBeFieldEnum.enum.lastName, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.lastName.label')}
            value={clientData.user.lastName}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.lastName, syncedFields)}
          />
        )}

        {/* Email — always locked (V1 exception) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.email, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.email.label')}
            value={clientData.user.email}
            isSynchronized={false}
          />
        )}

        {/* Phone number */}
        {isFieldVisible(ProfileBeFieldEnum.enum.phone, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.phoneNumber.label')}
            value={formatPhoneNumberDisplay(clientData.phone)}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.phone, syncedFields)}
            pmsTooltip={getPhonePmsTooltip(t)}
          />
        )}

        {/* Date of birth */}
        {isFieldVisible(ProfileBeFieldEnum.enum.birth, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.dateOfBirth.label')}
            value={formatDateOfBirth(clientData.birth, t)}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.birth, syncedFields)}
          />
        )}
      </GeneralInformationGroup>
      <GeneralInformationGroup group="residence" hiddenFields={hiddenFields} userRole={userRole}>
        {/* Address */}
        {isFieldVisible(ProfileBeFieldEnum.enum.street, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.address.label')}
            value={clientData.street}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.street, syncedFields)}
          />
        )}

        {/* Zip code */}
        {isFieldVisible(ProfileBeFieldEnum.enum.zipCode, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.zipCode.label')}
            value={clientData.zipCode}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.zipCode, syncedFields)}
          />
        )}

        {/* Building number — always locked (V1 exception) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.buildingNumber, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.buildingNumber.label')}
            value={clientData.buildingNumber ?? EMPTY_VALUE}
            isSynchronized={false}
          />
        )}

        {/* Building — Administrator and Manager only */}
        {canChangeBuilding(userRole) && (
          <LockedFieldRow
            label={t('clients:generalInformation.buildingId.label')}
            value={clientData.buildingId.name ?? EMPTY_VALUE}
            isSynchronized={false}
          />
        )}

        {/* Unit */}
        {isFieldVisible(ProfileBeFieldEnum.enum.apartment, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.unit.label')}
            value={clientData.apartment ? String(clientData.apartment) : null}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.apartment, syncedFields)}
          />
        )}

        {/* Floor */}
        {isFieldVisible(ProfileBeFieldEnum.enum.floor, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.floor.label')}
            value={clientData.floor ? String(clientData.floor) : null}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.floor, syncedFields)}
          />
        )}
      </GeneralInformationGroup>
      <GeneralInformationGroup group="lease" hiddenFields={hiddenFields} userRole={userRole}>
        {/* Move-in date */}
        {isFieldVisible(ProfileBeFieldEnum.enum.moveInDate, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.moveInDate.label')}
            value={
              clientData.moveInDate ? formatDateOnly(new Date(clientData.moveInDate)) : EMPTY_VALUE
            }
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.moveInDate, syncedFields)}
          />
        )}

        {/* Move-out date */}
        {isFieldVisible(ProfileBeFieldEnum.enum.moveOutDate, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.moveOutDate.label')}
            value={
              clientData.moveOutDate
                ? formatDateOnly(new Date(clientData.moveOutDate))
                : EMPTY_VALUE
            }
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.moveOutDate, syncedFields)}
          />
        )}

        {/* Lease start date */}
        {isFieldVisible(ProfileBeFieldEnum.enum.leaseStart, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.leaseStartDate.label')}
            value={
              clientData.leaseStart ? formatDateOnly(new Date(clientData.leaseStart)) : EMPTY_VALUE
            }
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.leaseStart, syncedFields)}
          />
        )}

        {/* Lease end date */}
        {isFieldVisible(ProfileBeFieldEnum.enum.leaseEnd, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.leaseEndDate.label')}
            value={
              clientData.leaseEnd ? formatDateOnly(new Date(clientData.leaseEnd)) : EMPTY_VALUE
            }
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.leaseEnd, syncedFields)}
          />
        )}
      </GeneralInformationGroup>
      <GeneralInformationGroup group="other" hiddenFields={hiddenFields} userRole={userRole}>
        {/* Pets */}
        {isFieldVisible(ProfileBeFieldEnum.enum.pets, hiddenFields) && (
          <LockedFieldRow
            label={t('clients:generalInformation.pets.label')}
            value={clientData.pets != null ? formatBooleanValue(clientData.pets, t) : EMPTY_VALUE}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.pets, syncedFields)}
          />
        )}

        {/* In house delivery — the fallback shown when Door-to-Door isn't effectively available */}
        {isFieldVisible(null, hiddenFields) && showInHouseDelivery && (
          <LockedFieldRow
            label={t('clients:generalInformation.inHouseDelivery.label')}
            value={
              clientData.inHouse != null ? formatBooleanValue(clientData.inHouse, t) : EMPTY_VALUE
            }
            isSynchronized={isFieldSynced(null, syncedFields)}
          />
        )}

        {/* Tags */}
        {isFieldVisible(null, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.tags.label')}
              </Typography>
            </S.LabelWrapper>
            <S.FieldValue $isViewOnly>
              {(clientData.tags ?? []).length > 0 ? (
                <S.TagsWrapper
                  $hasOverflowBadge={(clientData.tags ?? []).length > MAX_VISIBLE_CHIPS}
                >
                  {(clientData.tags ?? []).slice(0, MAX_VISIBLE_CHIPS).map((tag) => (
                    <Badge key={tag} color="blue" size="small" variant="light">
                      {tag}
                    </Badge>
                  ))}
                  {(clientData.tags ?? []).length > MAX_VISIBLE_CHIPS && (
                    <S.TagsQuantityBadge quantity={(clientData.tags ?? []).length} />
                  )}
                </S.TagsWrapper>
              ) : (
                EMPTY_VALUE
              )}
            </S.FieldValue>
          </S.FieldRow>
        )}

        {/* App User */}
        {clientData.buildingId.appUserStatusAvailable && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.appUser.label')}
              </Typography>
            </S.LabelWrapper>
            <S.FieldValue $isViewOnly>
              <Badge color={isAppUser ? 'green' : 'red'} variant="light" size="small">
                {isAppUser ? t('common:booleans.yes') : t('common:booleans.no')}
              </Badge>
            </S.FieldValue>
          </S.FieldRow>
        )}

        {/* Last App Login */}
        {clientData.buildingId.appUserStatusAvailable && (
          <LockedFieldRow
            label={t('clients:generalInformation.lastAppLogin.label')}
            value={isAppUser ? formatDateLong(new Date(clientData.lastAppLogin!)) : EMPTY_VALUE}
            isSynchronized={false}
          />
        )}

        {/* PMS ID — visible when client has a PMS ID, or field is synced (shows EMPTY_VALUE) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.pmsExternalId, hiddenFields) &&
          (pmsIdInfo !== null ||
            isFieldSynced(ProfileBeFieldEnum.enum.pmsExternalId, syncedFields)) && (
            <LockedFieldRow
              label={
                pmsIdInfo
                  ? t('clients:generalInformation.pmsId.labelWithPmsName', {
                      pmsName: pmsIdInfo.pmsName,
                    })
                  : t('clients:generalInformation.pmsId.label')
              }
              value={pmsIdInfo?.pmsExternalId}
              isSynchronized={true}
            />
          )}

        {/* Mailing list */}
        {isFieldVisible(null, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.mailingList.label')}
              </Typography>
            </S.LabelWrapper>
            <S.FieldValue $isViewOnly>
              {clientMailingLists.length > 0 ? (
                <S.TagsWrapper $hasOverflowBadge={clientMailingLists.length > MAX_VISIBLE_CHIPS}>
                  {sortedMailingLists.slice(0, MAX_VISIBLE_CHIPS).map((item) => (
                    <Badge
                      key={item.id}
                      color={
                        PROTECTED_MAILING_LIST_IDS.includes(String(item.id)) ? 'neutral' : 'purple'
                      }
                      size="small"
                      variant="light"
                    >
                      {item.value}
                    </Badge>
                  ))}
                  {clientMailingLists.length > MAX_VISIBLE_CHIPS && (
                    <S.TagsQuantityBadge quantity={clientMailingLists.length} />
                  )}
                </S.TagsWrapper>
              ) : (
                EMPTY_VALUE
              )}
            </S.FieldValue>
          </S.FieldRow>
        )}

        {/* Vehicles */}
        {isFieldVisible(null, hiddenFields) && userRole !== UserRoleEnum.enum.Trainer && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.vehicles.label')}
              </Typography>
            </S.LabelWrapper>
            <S.FieldValue>
              <S.FlushFieldButton
                size="small"
                use="secondary"
                onClick={() =>
                  popupFactory.open(ResidentVehiclesModal, { residentId: Number(clientData.id) })
                }
              >
                <Icon icon={GearSixIcon} size="1.6rem" />
                {t('clients:generalInformation.vehicles.manage')}
              </S.FlushFieldButton>
            </S.FieldValue>
          </S.FieldRow>
        )}
      </GeneralInformationGroup>
    </FieldsTable>
  )
}
